import { useCallback, useEffect, useMemo, useState } from "react";

import { useFetcher, useLocation, useSearchParams } from "@remix-run/react";

import { isExitSurveyEnabledClient } from "~/lib/exit-survey";
import {
    canShowExitSurvey,
    markExitSurveySessionAutoOpened,
    type ExitSurveyTriggerId,
} from "~/lib/exit-survey-gate";

import { ExitSurveyDialog } from "./ExitSurveyDialog";
import { useExitSurveyTriggers } from "./useExitSurveyTriggers";

type CartApi = { cart: { packages: unknown[]; spaces: unknown[] } | null } | undefined;

/**
 * Triggers (all call the same tryOpen path when canShowExitSurvey + base gates pass):
 * - exit_intent: (pointer: fine) + mouseout on documentElement, clientY <= 0
 * - idle: store layout only; 45s no keydown/pointerdown/scroll on window
 * - leave_cart: left /cart with line items (sessionStorage bridges store → checkout)
 *
 * Session: first successful open sets sessionStorage auto-opened (one auto-open per tab session).
 * Snooze: close without submit → 24h localStorage. Submit → 90d suppress via localStorage.
 * VITE_EXIT_SURVEY_ENABLED=false disables client + API.
 */
export function ExitSurveyHost({ layout }: { layout: "store" | "checkout" }) {
    const [open, setOpen] = useState(false);
    const [submitTrigger, setSubmitTrigger] = useState<ExitSurveyTriggerId | null>(null);
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const cartFetcher = useFetcher<CartApi>();

    useEffect(() => {
        cartFetcher.load("/api/cart");
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only cart hydration

    const contactUsOpen = Boolean(searchParams.get("contactUs"));
    const suppressOrders = location.pathname.startsWith("/orders");

    const cartLoaded = cartFetcher.state === "idle" && cartFetcher.data !== undefined;
    const itemCount = useMemo(() => {
        const c = cartFetcher.data?.cart;
        if (!c) return 0;
        return c.packages.length + c.spaces.length;
    }, [cartFetcher.data]);

    const scenario = itemCount > 0 ? "CART_ABANDON" : "BROWSE_ABANDON";

    const pageUrl =
        typeof window !== "undefined" ? `${window.location.origin}${location.pathname}${location.search}` : "";

    const baseEnabled =
        isExitSurveyEnabledClient() &&
        cartLoaded &&
        !contactUsOpen &&
        !suppressOrders &&
        !open;

    const handleOpenChange = useCallback((next: boolean) => {
        setOpen(next);
        if (!next) setSubmitTrigger(null);
    }, []);

    const tryOpen = useCallback(
        (trigger: ExitSurveyTriggerId) => {
            if (!baseEnabled) return;
            if (!canShowExitSurvey()) return;
            markExitSurveySessionAutoOpened();
            setSubmitTrigger(trigger);
            setOpen(true);
            if (import.meta.env.DEV && typeof console !== "undefined" && console.debug) {
                console.debug("[exit-survey] opened via", trigger);
            }
        },
        [baseEnabled],
    );

    useExitSurveyTriggers({
        tryOpen,
        baseEnabled,
        layout,
        pathname: location.pathname,
        itemCount,
        cartLoaded,
    });

    if (!isExitSurveyEnabledClient()) {
        return null;
    }

    return (
        <ExitSurveyDialog
            open={open}
            onOpenChange={handleOpenChange}
            scenario={scenario}
            pageUrl={pageUrl}
            triggerId={submitTrigger}
        />
    );
}
